import { canLayerOverrideProperty, defaultTenantBrandingPolicy, themeCapabilityRegistry, } from "./catalog";
import { isThemeHexColor, themeColorDistance, themeContrastRatio, } from "./color";
export const themeValidationRulesetVersion = "1.0.0";
const unsafeString = /[{};<>]|javascript:|data:|url\s*\(/i;
const colorKeys = new Set([
    "brand.primary",
    "brand.secondary",
    "color.page.background",
    "color.surface.default",
    "color.surface.raised",
    "color.surface.sunken",
    "color.surface.overlay",
    "color.text.primary",
    "color.text.secondary",
    "color.text.muted",
    "color.text.inverse",
    "color.text.disabled",
    "color.text.link",
    "color.interaction.hover",
    "color.interaction.selected",
    "color.interaction.focus",
    "color.interaction.disabled",
    "color.interaction.drag",
    "color.status.information",
    "color.status.success",
    "color.status.warning",
    "color.status.error",
    "color.status.neutral",
    "color.border.default",
    "color.border.subtle",
    "color.border.strong",
    "color.border.focus",
    "color.border.error",
    "chart.axis",
    "chart.grid",
    "chart.label",
    "chart.tooltip",
]);
const numberRanges = {
    "typography.scale": [0.875, 1.25],
    "typography.weight.regular": [300, 500],
    "typography.weight.medium": [500, 700],
    "typography.weight.bold": [600, 900],
    "typography.lineHeight": [1.2, 2],
    "spacing.scale": [0.75, 1.25],
    "control.height": [32, 56],
    "grid.rowHeight": [36, 72],
    "shape.radius.small": [0, 16],
    "shape.radius.medium": [0, 24],
    "shape.radius.large": [0, 32],
    "motion.durationScale": [0, 2],
};
const enumValues = {
    "appearance.mode": ["light", "dark", "system"],
    "appearance.colorScheme": ["light", "dark"],
    "appearance.contrastMode": ["standard", "high", "system"],
    "density.mode": ["compact", "standard", "comfortable"],
    "visualization.cardStyle": ["outlined", "elevated", "flat"],
    "visualization.hoverEffect": ["none", "lift", "glow", "scale"],
    "ui.buttonStyle": ["solid", "outlined", "subtle"],
    "ui.formStyle": ["outlined", "filled", "minimal"],
    "ui.tableStyle": ["standard", "striped"],
    "ui.navigationStyle": ["standard", "soft", "pill"],
    "accessibility.colorVisionMode": [
        "none",
        "protanopia",
        "deuteranopia",
        "tritanopia",
        "grayscale",
    ],
    "chart.decalMode": ["auto", "always", "off"],
    "motion.enabled": [true, false],
    "motion.reduced": [true, false],
    "accessibility.highContrast": [true, false],
    "accessibility.reducedMotion": [true, false],
    "accessibility.enhancedFocus": [true, false],
};
const validAssetReference = (value) => /^asset:\/\/(?:inteacc|tenant)\/[a-z0-9][a-z0-9._/-]{0,127}$/i.test(value);
const validateAsset = (value, policy, assets) => {
    if (!validAssetReference(value)) {
        return {
            valid: false,
            code: "unsafe-asset-reference",
            message: "Brand assets must use an approved asset:// reference.",
        };
    }
    const asset = assets.find((candidate) => candidate.reference === value);
    if (!asset) {
        return value.startsWith("asset://inteacc/")
            ? { valid: true }
            : {
                valid: false,
                code: "unknown-asset-reference",
                message: "The tenant asset reference is not in the approved catalog.",
            };
    }
    const aspectRatio = Math.max(asset.width, asset.height) / Math.max(1, Math.min(asset.width, asset.height));
    const scheme = value.slice(0, value.indexOf(":"));
    const origin = value.split("/")[2] ?? "";
    if (!policy.assetPolicy.allowedTypes.includes(asset.type) ||
        asset.bytes > policy.assetPolicy.maximumBytes ||
        asset.width < policy.assetPolicy.minimumWidth ||
        asset.height < policy.assetPolicy.minimumHeight ||
        aspectRatio > policy.assetPolicy.maximumAspectRatio ||
        !policy.assetPolicy.permittedReferenceSchemes.includes(scheme) ||
        !policy.assetPolicy.permittedOrigins.includes(origin)) {
        return {
            valid: false,
            code: "asset-policy-failure",
            message: "The asset does not meet tenant branding metadata policy.",
        };
    }
    return { valid: true };
};
export const validateThemePropertyValue = (key, value, context) => {
    const policy = context.tenantPolicy ?? defaultTenantBrandingPolicy;
    if (!canLayerOverrideProperty(context.layer, key, policy)) {
        return {
            valid: false,
            code: "scope-not-allowed",
            message: `${context.layer} scope cannot override ${key}.`,
        };
    }
    if (colorKeys.has(key)) {
        return isThemeHexColor(value)
            ? { valid: true }
            : {
                valid: false,
                code: "invalid-color",
                message: `${key} must be a six-digit hexadecimal color.`,
            };
    }
    if (key === "chart.seriesPalette") {
        if (!Array.isArray(value) ||
            value.length < 3 ||
            value.length > 12 ||
            !value.every(isThemeHexColor)) {
            return {
                valid: false,
                code: "invalid-chart-palette",
                message: "Chart palettes require 3–12 hexadecimal colors.",
            };
        }
        return { valid: true };
    }
    if (key === "brand.logoReference") {
        return typeof value === "string"
            ? validateAsset(value, policy, context.assets ?? [])
            : {
                valid: false,
                code: "invalid-asset-reference",
                message: "Logo reference must be a string.",
            };
    }
    const range = numberRanges[key];
    if (range) {
        return typeof value === "number" &&
            Number.isFinite(value) &&
            value >= range[0] &&
            value <= range[1]
            ? { valid: true }
            : {
                valid: false,
                code: "out-of-range",
                message: `${key} must be between ${range[0]} and ${range[1]}.`,
            };
    }
    const allowed = enumValues[key];
    if (allowed) {
        return allowed.includes(value)
            ? { valid: true }
            : {
                valid: false,
                code: "invalid-enumeration",
                message: `${key} has an unsupported value.`,
            };
    }
    if (typeof value === "string") {
        if (value.length > 256 ||
            unsafeString.test(value) ||
            /^(?:https?|file):/i.test(value)) {
            return {
                valid: false,
                code: "unsafe-string",
                message: `${key} contains unsafe or unsupported content.`,
            };
        }
        if (key === "typography.family.primary" &&
            context.layer === "tenant" &&
            !policy.allowedFontFamilies.some((family) => value.includes(family))) {
            return {
                valid: false,
                code: "font-not-allowlisted",
                message: "Tenant font is not in the approved font allowlist.",
            };
        }
        return { valid: value.trim().length > 0 || key === "brand.tenantDisplayName" };
    }
    return {
        valid: false,
        code: "invalid-type",
        message: `${key} has an unsupported value type.`,
    };
};
const issue = (sourceId, layer, key, validation) => ({
    issueId: `${sourceId}:${key}:${validation.code ?? "invalid"}`,
    code: validation.code ?? "invalid",
    severity: "error",
    message: validation.message ?? `${key} is invalid.`,
    propertyKeys: [key],
    sourceId,
    layer,
    remediation: "Remove the override or choose an approved semantic value.",
});
export const validateThemePreference = (preference, context) => {
    const validProperties = [];
    const invalidProperties = [];
    const issues = [];
    for (const [untrustedKey, value] of Object.entries(preference.values)) {
        if (!themeCapabilityRegistry.isRegistered(untrustedKey))
            continue;
        const validation = validateThemePropertyValue(untrustedKey, value, context);
        if (validation.valid) {
            validProperties.push(untrustedKey);
        }
        else {
            invalidProperties.push(untrustedKey);
            issues.push(issue(context.sourceId, context.layer, untrustedKey, validation));
        }
    }
    const unknownProperties = preference.unknownProperties ?? [];
    for (const property of unknownProperties) {
        issues.push({
            issueId: `${context.sourceId}:unknown:${property.key}`,
            code: "unknown-property",
            severity: "warning",
            message: `${property.key} is preserved but not registered and does not affect resolution.`,
            propertyKeys: [],
            sourceId: context.sourceId,
            layer: context.layer,
            remediation: "Register and migrate the property before using it.",
        });
    }
    return {
        rulesetVersion: themeValidationRulesetVersion,
        schemaVersion: preference.schemaVersion,
        valid: invalidProperties.length === 0,
        canPreview: true,
        canSave: invalidProperties.length === 0,
        issues,
        accessibilityIssues: [],
        validProperties,
        invalidProperties,
        unknownProperties,
    };
};
const accessibilityIssue = (rule, issueId, message, propertyKeys, severity, measured, required, wcagCriteria, remediation, evaluation = "automated") => ({
    issueId,
    code: rule,
    severity,
    message,
    propertyKeys,
    remediation,
    rule,
    wcagCriteria,
    measured,
    required,
    evaluation,
});
const contrastCheck = (values, foregroundKey, backgroundKey, minimum, rule, label) => {
    const foreground = String(values[foregroundKey]);
    const background = String(values[backgroundKey]);
    const ratio = themeContrastRatio(foreground, background);
    if (ratio >= minimum)
        return undefined;
    return accessibilityIssue(rule, `${rule}:${foregroundKey}:${backgroundKey}`, `${label} contrast is ${ratio.toFixed(2)}:1.`, [foregroundKey, backgroundKey], "blocking-accessibility", `${ratio.toFixed(2)}:1`, `${minimum}:1`, rule === "text-contrast" ? ["1.4.3"] : ["1.4.11", "2.4.11"], `Choose semantic colors with at least ${minimum}:1 contrast.`);
};
export const validateResolvedThemeAccessibility = (values) => {
    const issues = [];
    const mandatoryChecks = [
        contrastCheck(values, "color.text.primary", "color.surface.default", 4.5, "text-contrast", "Primary text"),
        contrastCheck(values, "color.text.secondary", "color.surface.default", 4.5, "text-contrast", "Secondary text"),
        contrastCheck(values, "color.text.link", "color.surface.default", 4.5, "text-contrast", "Link text"),
        contrastCheck(values, "color.border.strong", "color.surface.default", 3, "control-contrast", "Essential control boundary"),
        contrastCheck(values, "color.border.focus", "color.surface.default", 3, "focus-visibility", "Focus indicator"),
        contrastCheck(values, "color.border.focus", "color.page.background", 3, "focus-visibility", "Focus indicator on page"),
    ];
    for (const check of mandatoryChecks) {
        if (check)
            issues.push(check);
    }
    const disabledRatio = themeContrastRatio(values["color.text.disabled"], values["color.surface.default"]);
    if (disabledRatio < 3) {
        issues.push(accessibilityIssue("disabled-state", "disabled-state:text", `Disabled text is subtle at ${disabledRatio.toFixed(2)}:1.`, ["color.text.disabled", "color.surface.default"], "warning", `${disabledRatio.toFixed(2)}:1`, "IAF review threshold 3:1", ["1.4.3"], "Verify disabled content remains discernible without resembling enabled content.", "simulated"));
    }
    const selectedRatio = themeContrastRatio(values["color.interaction.selected"], values["color.surface.default"]);
    if (selectedRatio < 1.2) {
        issues.push(accessibilityIssue("selected-state", "selected-state:surface", "Selected surface may be difficult to distinguish.", ["color.interaction.selected", "color.surface.default"], "warning", `${selectedRatio.toFixed(2)}:1`, "IAF review threshold 1.2:1 plus non-color state", ["1.4.1", "1.4.11"], "Use an additional border, icon, or semantic selected indicator.", "simulated"));
    }
    const palette = values["chart.seriesPalette"];
    for (let first = 0; first < palette.length; first += 1) {
        for (let second = first + 1; second < palette.length; second += 1) {
            const firstColor = palette[first];
            const secondColor = palette[second];
            if (firstColor &&
                secondColor &&
                themeColorDistance(firstColor, secondColor) < 72) {
                issues.push(accessibilityIssue("chart-series-distinction", `chart-series:${first}:${second}`, `Chart series ${first + 1} and ${second + 1} may be visually similar.`, ["chart.seriesPalette"], "warning", themeColorDistance(firstColor, secondColor).toFixed(0), "IAF distance guidance 72 plus symbols/decals", ["1.4.1"], "Retain symbols, labels, decals, summary, and table fallback.", "simulated"));
            }
        }
    }
    if (values["accessibility.colorVisionMode"] !== "none") {
        issues.push({
            ...accessibilityIssue("color-vision", "color-vision:human-review", "Color-vision assistance is a simulation and requires human review.", ["accessibility.colorVisionMode", "chart.seriesPalette"], "information", values["accessibility.colorVisionMode"], "Meaning preserved without color alone", ["1.4.1"], "Review statuses and charts with labels, icons, shapes, symbols, and tables.", "manual"),
            humanReviewGuidance: "Automated simulation cannot certify perception or WCAG compliance.",
        });
    }
    return issues;
};
//# sourceMappingURL=validation.js.map